import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import * as THREE from 'three';
import './CelestialSphere.css';

/**
 * True celestial-sphere view.
 *
 * The previous renderer projected everything onto a flat plane — gnomonic under
 * 60° FOV, equirectangular beyond. Equirectangular is a rectangle, so zooming
 * out produced a flat map with no curvature and no edge to the sky, and gave no
 * sense of how small a 0.2° corridor really is.
 *
 * Here every object sits at its real direction on a unit sphere and the camera
 * sits at the centre looking outward. That is worth stating plainly: a pinhole
 * camera at the centre of a sphere IS the gnomonic projection. So the close-in
 * view is mathematically the same as before, while zooming out now yields the
 * correct spherical geometry for free — constellation boundaries curve, meridians
 * converge toward the poles, and the sky closes into a dome.
 *
 * Coordinate convention: +Z is the north celestial pole, and camera.up is set to
 * it, so celestial north is up and right ascension increases to the left when
 * looking outward — the orientation used on every sky chart.
 */

const DEG = Math.PI / 180;
const R = 1000;             // sphere radius; arbitrary, just needs to exceed near-plane
const FOV_MIN = 0.05;       // tighter than the band itself
const FOV_MAX = 140;        // beyond this perspective distortion stops being useful
const LABEL_LIMIT = 12;     // named stars to label at once

function toVec(ra, dec, r = R) {
  const d = dec * DEG, a = ra * DEG;
  const cd = Math.cos(d);
  return new THREE.Vector3(r * cd * Math.cos(a), r * cd * Math.sin(a), r * Math.sin(d));
}

/** Subdivide an RA/Dec segment so it curves correctly once mapped to the sphere. */
function densify(ra1, dec1, ra2, dec2, maxStepDeg = 1) {
  let dRa = ra2 - ra1;
  if (dRa > 180) dRa -= 360;
  if (dRa < -180) dRa += 360;
  const dDec = dec2 - dec1;
  const span = Math.max(Math.abs(dRa) * Math.cos(((dec1 + dec2) / 2) * DEG), Math.abs(dDec));
  const steps = Math.max(1, Math.ceil(span / maxStepDeg));
  const out = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    out.push([ra1 + dRa * t, dec1 + dDec * t]);
  }
  return out;
}

function spectralColor(teff) {
  if (!teff) return '#ffffff';
  if (teff >= 30000) return '#9bb0ff';
  if (teff >= 10000) return '#aabfff';
  if (teff >= 7500) return '#cad7ff';
  if (teff >= 6000) return '#fbf8ff';
  if (teff >= 5200) return '#fff4e8';
  if (teff >= 3700) return '#ffd2a1';
  return '#ffb56c';
}

const BODY_STYLE = {
  Sun:     { color: '#fde68a', size: 22 },
  Moon:    { color: '#e2e8f0', size: 18 },
  Mercury: { color: '#cbd5e1', size: 9 },
  Venus:   { color: '#fef3c7', size: 13 },
  Mars:    { color: '#fca5a5', size: 11 },
  Jupiter: { color: '#fed7aa', size: 15 },
  Saturn:  { color: '#fde68a', size: 13 },
  Uranus:  { color: '#a5f3fc', size: 9 },
  Neptune: { color: '#a5b4fc', size: 9 },
};

export default function CelestialSphere({
  stars = [],
  constellations = [],
  zenithRa = 0,
  zenithDec = 0,
  ribbonBounds = null,
  birthDatetime = null,
  bandId = null,
}) {
  const mountRef = useRef(null);
  const stateRef = useRef({ ra: zenithRa, dec: zenithDec, fov: 1.2, targetFov: 1.2 });
  const centredRef = useRef(false);
  const sceneRefs = useRef({});
  const [fovDisplay, setFovDisplay] = useState(1.2);
  const [showLabels, setShowLabels] = useState(false);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [labels, setLabels] = useState([]);
  const [sky, setSky] = useState(null);
  const [showBodies, setShowBodies] = useState(true);
  const [showBright, setShowBright] = useState(true);

  const named = useRef([]);

  // ---------------------------------------------------------------- zoom API
  const zoomBy = useCallback((factor) => {
    const s = stateRef.current;
    s.targetFov = Math.min(FOV_MAX, Math.max(FOV_MIN, s.targetFov * factor));
  }, []);

  const reset = useCallback(() => {
    const s = stateRef.current;
    s.ra = zenithRa; s.dec = zenithDec; s.targetFov = 1.2;
  }, [zenithRa, zenithDec]);

  const wholeSky = useCallback(() => {
    stateRef.current.targetFov = FOV_MAX;
  }, []);

  // Sun, Moon, planets and recognisable stars for this band's moment.
  useEffect(() => {
    if (!bandId) return;
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/zenith/band/${bandId}/sky`)
      .then(({ data }) => { if (!cancelled) setSky(data); })
      .catch(() => { /* the sky view stays useful without them */ });
    return () => { cancelled = true; };
  }, [bandId]);

  // ------------------------------------------------------------------ scene
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      stateRef.current.fov, mount.clientWidth / mount.clientHeight, 0.1, R * 4
    );
    camera.up.set(0, 0, 1);            // celestial north is up
    camera.position.set(0, 0, 0);      // observer at the centre of the sphere

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setClearColor(0x02020a, 1);
    mount.appendChild(renderer.domElement);

    // ---- stars ----------------------------------------------------------
    const valid = stars.filter((s) => Number.isFinite(s.ra) && Number.isFinite(s.dec));
    const pos = new Float32Array(valid.length * 3);
    const col = new Float32Array(valid.length * 3);
    const siz = new Float32Array(valid.length);
    const c = new THREE.Color();
    const mags = valid.map((s) => s.magnitude ?? 15).filter((m) => m > 0 && m < 25);
    const magMin = mags.length ? Math.min(...mags) : 0;

    valid.forEach((s, i) => {
      const v = toVec(s.ra, s.dec);
      pos[i * 3] = v.x; pos[i * 3 + 1] = v.y; pos[i * 3 + 2] = v.z;
      c.set(spectralColor(s.teff || s.temp_k));
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
      siz[i] = Math.max(1.2, 5.5 * Math.pow(10, -0.16 * ((s.magnitude ?? 15) - magMin)));
    });

    named.current = valid
      .map((s, i) => ({ ...s, _i: i }))
      .filter((s) => s.proper || s.proper_name || s.name)
      .sort((a, b) => (a.magnitude ?? 99) - (b.magnitude ?? 99))
      .slice(0, LABEL_LIMIT);

    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    starGeo.setAttribute('size', new THREE.BufferAttribute(siz, 1));

    // Point size scales with zoom so stars swell as you close in, but is clamped
    // so a wide view doesn't collapse into invisible specks.
    const starMat = new THREE.ShaderMaterial({
      uniforms: { uScale: { value: 1 } },
      vertexShader: `
        attribute float size;
        varying vec3 vColor;
        uniform float uScale;
        void main() {
          vColor = color;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = clamp(size * uScale, 1.0, 26.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float a = smoothstep(0.5, 0.06, d);
          gl_FragColor = vec4(vColor, a);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true,
    });
    const starField = new THREE.Points(starGeo, starMat);
    scene.add(starField);

    // ---- constellation boundaries ---------------------------------------
    // Densified in RA/Dec before mapping to the sphere. IAU boundaries run along
    // lines of constant RA or Dec, so interpolating in those coordinates traces
    // the true boundary — which is why they now curve instead of cutting chords.
    const boundGroup = new THREE.Group();
    const boundMat = new THREE.LineBasicMaterial({
      color: 0x3ddc97, transparent: true, opacity: 0.34,
    });
    constellations.forEach((con) => {
      const polys = con.geometry_type === 'MultiPolygon' ? con.coordinates : [con.coordinates];
      polys.forEach((poly) => poly.forEach((ring) => {
        const pts = [];
        for (let i = 0; i < ring.length - 1; i++) {
          const [ra1, d1] = ring[i], [ra2, d2] = ring[i + 1];
          densify(ra1, d1, ra2, d2).forEach(([ra, dec]) => pts.push(toVec(ra, dec, R * 0.995)));
        }
        if (pts.length > 1) {
          boundGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), boundMat));
        }
      }));
    });
    scene.add(boundGroup);

    // ---- the band itself -------------------------------------------------
    // The whole point of zooming out: an outline of the actual corridor, so its
    // size against the rest of the sky is visible rather than described.
    //
    // The outline is derived from the STARS, not from ribbon_bounds. Those two
    // disagree: the catalogue positions are precessed to the birth epoch while
    // ribbon_bounds are not, so drawing the supplied bounds puts the box a
    // measured ~0.28 deg away from the stars it is supposed to contain. Until
    // the backend returns both in one frame, the stars are the honest anchor —
    // they are what the viewer can actually see.
    const bandGroup = new THREE.Group();
    const starBounds = valid.length ? valid.reduce((acc, s) => ({
      ra_min: Math.min(acc.ra_min, s.ra), ra_max: Math.max(acc.ra_max, s.ra),
      dec_min: Math.min(acc.dec_min, s.dec), dec_max: Math.max(acc.dec_max, s.dec),
    }), { ra_min: Infinity, ra_max: -Infinity, dec_min: Infinity, dec_max: -Infinity }) : null;

    const bounds = starBounds || ribbonBounds;
    if (bounds) {
      const { ra_min, ra_max, dec_min, dec_max } = bounds;
      const edge = [];
      const corners = [
        [ra_min, dec_min], [ra_max, dec_min],
        [ra_max, dec_max], [ra_min, dec_max], [ra_min, dec_min],
      ];
      for (let i = 0; i < corners.length - 1; i++) {
        densify(corners[i][0], corners[i][1], corners[i + 1][0], corners[i + 1][1], 0.02)
          .forEach(([ra, dec]) => edge.push(toVec(ra, dec, R * 0.99)));
      }
      bandGroup.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(edge),
        new THREE.LineBasicMaterial({ color: 0xfbbf24, transparent: true, opacity: 0.95 })
      ));

      // Horizontal rule through the middle of the band, so it is obvious which
      // line was straight overhead. Uses the band's own centre rather than
      // zenithDec, for the same frame-mismatch reason as the outline.
      const midDec = (dec_min + dec_max) / 2;
      const mid = [];
      densify(ra_min, midDec, ra_max, midDec, 0.02)
        .forEach(([ra, dec]) => mid.push(toVec(ra, dec, R * 0.99)));
      bandGroup.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(mid),
        new THREE.LineBasicMaterial({ color: 0xfbbf24, transparent: true, opacity: 0.4 })
      ));
    }
    scene.add(bandGroup);

    // ---- zenith crosshair -------------------------------------------------
    const zg = new THREE.Group();
    const zMat = new THREE.LineBasicMaterial({ color: 0x67e8f9, transparent: true, opacity: 0.9 });
    const arm = 0.12;
    const cRa = bounds ? (bounds.ra_min + bounds.ra_max) / 2 : zenithRa;
    const cDec = bounds ? (bounds.dec_min + bounds.dec_max) / 2 : zenithDec;
    [[[-arm, 0], [arm, 0]], [[0, -arm], [0, arm]]].forEach(([[a1, d1], [a2, d2]]) => {
      const p = densify(cRa + a1 / Math.max(Math.cos(cDec * DEG), 0.05), cDec + d1,
                        cRa + a2 / Math.max(Math.cos(cDec * DEG), 0.05), cDec + d2, 0.01)
        .map(([ra, dec]) => toVec(ra, dec, R * 0.985));
      zg.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(p), zMat));
    });
    scene.add(zg);

    // ---- recognisable stars ------------------------------------------------
    // The 88 named stars brighter than magnitude 2.5, straight from the AT-HYG
    // registry. These are the anchors people actually know, and they are what
    // makes a zoomed-out sky legible rather than an undifferentiated dust.
    const brightGroup = new THREE.Group();
    if (sky?.bright_stars?.length) {
      const bp = new Float32Array(sky.bright_stars.length * 3);
      const bs = new Float32Array(sky.bright_stars.length);
      sky.bright_stars.forEach((st, i) => {
        const v = toVec(st.ra, st.dec, R * 0.98);
        bp[i * 3] = v.x; bp[i * 3 + 1] = v.y; bp[i * 3 + 2] = v.z;
        bs[i] = Math.max(4, 13 - st.magnitude * 3);
      });
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(bp, 3));
      g.setAttribute('size', new THREE.BufferAttribute(bs, 1));
      brightGroup.add(new THREE.Points(g, new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color('#dbeafe') } },
        vertexShader: `attribute float size; void main(){
          gl_PointSize = size;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
        fragmentShader: `uniform vec3 uColor; void main(){
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float a = smoothstep(0.5, 0.05, d);
          gl_FragColor = vec4(uColor, a * 0.85); }`,
        transparent: true, blending: THREE.AdditiveBlending, depthWrite: false,
      })));
    }
    scene.add(brightGroup);

    // ---- solar system ------------------------------------------------------
    // Drawn as discs rather than points so they read as objects with a size,
    // which is how people expect planets to look against a field of stars.
    const bodyGroup = new THREE.Group();
    (sky?.bodies || []).forEach((b) => {
      const style = BODY_STYLE[b.name] || { color: '#ffffff', size: 10 };
      const mat = new THREE.SpriteMaterial({
        color: new THREE.Color(style.color),
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
      });
      const sp = new THREE.Sprite(mat);
      const v = toVec(b.ra, b.dec, R * 0.97);
      sp.position.copy(v);
      sp.userData = { body: b, baseSize: style.size };
      bodyGroup.add(sp);
    });
    scene.add(bodyGroup);

    // Centre the view on the stars the first time they arrive.
    if (starBounds && !centredRef.current) {
      centredRef.current = true;
      stateRef.current.ra = (starBounds.ra_min + starBounds.ra_max) / 2;
      stateRef.current.dec = (starBounds.dec_min + starBounds.dec_max) / 2;
    }

    sceneRefs.current = { scene, camera, renderer, boundGroup, starMat, starField };

    // ---- look controls ----------------------------------------------------
    // OrbitControls orbits a target, which is meaningless for a camera sitting at
    // the centre of the sphere. Dragging must instead change where we look, with
    // sensitivity tied to FOV so the feel is identical at every zoom level.
    let dragging = false, lastX = 0, lastY = 0;

    const down = (e) => {
      dragging = true;
      lastX = (e.touches ? e.touches[0].clientX : e.clientX);
      lastY = (e.touches ? e.touches[0].clientY : e.clientY);
    };
    const move = (e) => {
      if (!dragging) return;
      const x = (e.touches ? e.touches[0].clientX : e.clientX);
      const y = (e.touches ? e.touches[0].clientY : e.clientY);
      const s = stateRef.current;
      const perPx = s.fov / mount.clientHeight;
      // Divide by cos(dec) so horizontal dragging covers the same apparent
      // distance near the poles, where meridians crowd together.
      s.ra -= (x - lastX) * perPx / Math.max(Math.cos(s.dec * DEG), 0.02);
      s.dec = Math.max(-89.9, Math.min(89.9, s.dec + (y - lastY) * perPx));
      lastX = x; lastY = y;
      if (e.cancelable) e.preventDefault();
    };
    const up = () => { dragging = false; };
    const wheel = (e) => {
      e.preventDefault();
      zoomBy(Math.exp(e.deltaY * 0.0012));
    };

    mount.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    mount.addEventListener('touchstart', down, { passive: true });
    mount.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    mount.addEventListener('wheel', wheel, { passive: false });

    // ---- loop --------------------------------------------------------------
    let raf;
    let lastFov = 0;
    let lastLabelAt = 0;
    const tmp = new THREE.Vector3();
    const loop = (now = 0) => {
      const s = stateRef.current;
      // Ease toward the target FOV so zooming glides rather than snapping.
      s.fov += (s.targetFov - s.fov) * 0.18;

      camera.fov = s.fov;
      camera.updateProjectionMatrix();
      camera.lookAt(toVec(s.ra, s.dec, 1));

      starMat.uniforms.uScale.value = Math.min(3.2, Math.max(0.55, 1.6 / Math.sqrt(s.fov)));
      boundGroup.visible = showBoundaries && s.fov > 2.5;   // clutter up close
      brightGroup.visible = showBright;
      bodyGroup.visible = showBodies;

      // Sprites are sized in world units, so they must be rescaled as the field
      // of view changes or a planet becomes a dot at wide zoom and a wall up close.
      const spriteScale = (s.fov / 45) * R * 0.02;
      bodyGroup.children.forEach((sp) => {
        const k = Math.max(0.6, sp.userData.baseSize / 10) * spriteScale;
        sp.scale.set(k, k, 1);
      });

      renderer.render(scene, camera);

      // React state is deliberately NOT written every frame — that would force a
      // re-render at 60fps for a readout that changes imperceptibly. Update only
      // when the value has actually moved, and throttle label projection.
      if (Math.abs(s.fov - lastFov) / s.fov > 0.004) {
        lastFov = s.fov;
        setFovDisplay(s.fov);
      }

      if ((showLabels || showBodies) && now - lastLabelAt > 120) {
        lastLabelAt = now;
        const out = [];
        const labelSources = [
          ...named.current.map((st) => ({ ra: st.ra, dec: st.dec, name: st.proper || st.proper_name || st.name, kind: 'star' })),
          ...(showBodies ? (sky?.bodies || []).map((b) => ({ ra: b.ra, dec: b.dec, name: b.name, kind: b.kind })) : []),
          ...(showBright && s.fov > 8 ? (sky?.bright_stars || []).slice(0, 40).map((b) => ({ ra: b.ra, dec: b.dec, name: b.name, kind: 'bright' })) : []),
        ];
        labelSources.forEach((st) => {
          tmp.copy(toVec(st.ra, st.dec));
          tmp.project(camera);
          if (tmp.z < 1 && Math.abs(tmp.x) < 1 && Math.abs(tmp.y) < 1) {
            out.push({
              name: st.name,
              kind: st.kind,
              x: (tmp.x * 0.5 + 0.5) * mount.clientWidth,
              y: (-tmp.y * 0.5 + 0.5) * mount.clientHeight,
            });
          }
        });
        setLabels(out);
      }

      raf = requestAnimationFrame(loop);
    };
    loop();

    if (!showLabels && !showBodies) setLabels([]);

    const onResize = () => {
      if (!mount.clientWidth) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      mount.removeEventListener('mousedown', down);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      mount.removeEventListener('touchstart', down);
      mount.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
      mount.removeEventListener('wheel', wheel);
      renderer.dispose();
      starGeo.dispose(); starMat.dispose();
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
      });
      if (renderer.domElement.parentNode) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stars, constellations, zenithRa, zenithDec, ribbonBounds, showLabels, showBoundaries, sky, showBodies, showBright]);

  const fovLabel = fovDisplay < 1
    ? `${(fovDisplay * 60).toFixed(1)}′`
    : `${fovDisplay.toFixed(1)}°`;

  return (
    <div className="cs">
      <div className="cs-stage" ref={mountRef} />

      {showLabels && labels.map((l, i) => (
        <span className={`cs-label cs-label-${l.kind || 'star'}`} key={i}
              style={{ left: l.x, top: l.y }}>{l.name}</span>
      ))}

      {/* Legend: the moment, and how wide a view you are currently looking at. */}
      <div className="cs-legend">
        <span className="cs-legend-k">Zenith</span>
        <span className="cs-legend-v">
          RA {zenithRa.toFixed(3)}° · Dec {zenithDec >= 0 ? '+' : ''}{zenithDec.toFixed(3)}°
        </span>
        {birthDatetime && (
          <>
            <span className="cs-legend-k">Moment</span>
            <span className="cs-legend-v">
              {new Date(birthDatetime).toLocaleString(undefined, {
                dateStyle: 'medium', timeStyle: 'short',
              })}
            </span>
          </>
        )}
        {sky?.daylight && (
          <>
            <span className="cs-legend-k">Sky</span>
            <span className="cs-legend-v">
              {sky.daylight}
              {sky.sun_altitude_deg != null && ` · sun ${sky.sun_altitude_deg > 0 ? '+' : ''}${sky.sun_altitude_deg}°`}
            </span>
          </>
        )}
        {sky?.moon_illumination != null && (
          <>
            <span className="cs-legend-k">Moon</span>
            <span className="cs-legend-v">{Math.round(sky.moon_illumination * 100)}% lit</span>
          </>
        )}
        <span className="cs-legend-k">Field</span>
        <span className="cs-legend-v cs-fov">{fovLabel}</span>
      </div>

      <div className="cs-controls">
        <button onClick={() => zoomBy(0.6)} title="Zoom in">+</button>
        <button onClick={() => zoomBy(1.7)} title="Zoom out">−</button>
        <button onClick={reset} title="Back to the band">Band</button>
        <button onClick={wholeSky} title="See the whole sky">Sky</button>
      </div>

      <div className="cs-toggles">
        <label>
          <input type="checkbox" checked={showBoundaries}
                 onChange={(e) => setShowBoundaries(e.target.checked)} />
          Constellations
        </label>
        <label>
          <input type="checkbox" checked={showLabels}
                 onChange={(e) => setShowLabels(e.target.checked)} />
          Star names
        </label>
        <label>
          <input type="checkbox" checked={showBodies}
                 onChange={(e) => setShowBodies(e.target.checked)} />
          Planets
        </label>
        <label>
          <input type="checkbox" checked={showBright}
                 onChange={(e) => setShowBright(e.target.checked)} />
          Bright stars
        </label>
      </div>

      <p className="cs-hint">Drag to look · scroll to zoom</p>
    </div>
  );
}
